/**
 * The line that adds a property to a deal: its name (required), city and state, and, from a
 * single-property deal's Underwriting Models card, the model it is filed with.
 *
 * A single-property deal's "Add UW Model" (on each side of the card) and a portfolio's
 * "+ Add property" both open it, and both send what it asks to `POST /loans/{id}/properties`;
 * what happens to the model is the caller's. Nothing is sent until the line is complete.
 */

import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

import type { NewPropertyInput } from '@/services/portfolio'
import { MAX_WORKBOOK_UPLOAD_BYTES, type UnderwritingWorkbookVersion } from '@/services/underwritingWorkbooks'

/** The model choice that uploads a workbook instead of filing one already on the deal. */
export const UPLOAD_CHOICE = '__upload__'

/** The model a new property is filed with: a workbook already on the deal, or one to upload. */
export type ChosenModel = { versionId: string } | { file: File }

export type AddPropertyRequest = { property: NewPropertyInput; model: ChosenModel | null }

const FIELD =
  'w-full min-w-0 rounded border border-stone-700 bg-black/40 px-1.5 py-0.5 text-[10px] text-stone-200 outline-none focus:border-amber-300/50 disabled:opacity-50'
const CAPTION = 'mb-0.5 block text-[8.5px] uppercase tracking-wider text-stone-500'

export function AddPropertyForm({
  label,
  heading,
  models,
  note,
  busy,
  progress = null,
  error = null,
  onSubmit,
  onCancel,
}: {
  /** The form's name for assistive technology, e.g. "Add a property (Sponsor Model)". */
  label: string
  heading: string
  /**
   * The workbooks on the deal the new property may be filed with. When given, a model is
   * required: one of these, or a workbook to upload.
   */
  models?: UnderwritingWorkbookVersion[]
  /** What adding the property does, said before it is done. */
  note?: ReactNode
  busy: boolean
  /** What the request in flight is doing. */
  progress?: string | null
  /** Why the last request was refused. */
  error?: string | null
  onSubmit: (request: AddPropertyRequest) => void
  onCancel: () => void
}) {
  const id = useId()
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  // With no workbook on the deal to choose, the only choice is an upload.
  const [choice, setChoice] = useState(models && models.length === 0 ? UPLOAD_CHOICE : '')
  const [file, setFile] = useState<File | null>(null)
  const [invalid, setInvalid] = useState<string | null>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const property = name.trim()
    let model: ChosenModel | null = null
    let refusal: string | null = property ? null : 'Name the property.'
    if (!refusal && models) {
      if (choice === UPLOAD_CHOICE && file) {
        refusal = file.size > MAX_WORKBOOK_UPLOAD_BYTES
          ? `Workbook exceeds the ${MAX_WORKBOOK_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`
          : null
        model = { file }
      } else if (choice === UPLOAD_CHOICE) {
        refusal = 'Choose a workbook to upload.'
      } else if (choice) {
        model = { versionId: choice }
      } else {
        refusal = 'Choose a model, or a workbook to upload.'
      }
    }
    setInvalid(refusal)
    if (refusal) return
    onSubmit({
      property: {
        name: property,
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(state.trim() ? { state: state.trim() } : {}),
      },
      model,
    })
  }

  const shown = invalid ?? error

  return (
    <form
      role="group"
      aria-label={label}
      noValidate
      onSubmit={submit}
      className="mt-2 space-y-1.5 rounded border border-amber-400/25 bg-amber-400/[0.05] p-2 text-[9.5px] leading-relaxed text-amber-100/90"
    >
      <p className="text-[10px] font-medium text-amber-200">{heading}</p>
      <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_3.25rem] gap-1.5">
        <div className="min-w-0">
          <label htmlFor={`${id}-name`} className={CAPTION}>Property name</label>
          <input
            id={`${id}-name`}
            value={name}
            maxLength={200}
            required
            autoFocus
            disabled={busy}
            onChange={(event) => setName(event.target.value)}
            className={FIELD}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={`${id}-city`} className={CAPTION}>City</label>
          <input
            id={`${id}-city`}
            value={city}
            maxLength={120}
            disabled={busy}
            onChange={(event) => setCity(event.target.value)}
            className={FIELD}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={`${id}-state`} className={CAPTION}>State</label>
          <input
            id={`${id}-state`}
            value={state}
            maxLength={60}
            disabled={busy}
            onChange={(event) => setState(event.target.value)}
            className={FIELD}
          />
        </div>
      </div>

      {models ? (
        <div className="min-w-0">
          <label htmlFor={`${id}-model`} className={CAPTION}>Model</label>
          <div className="flex flex-wrap items-center gap-1.5">
            <select
              id={`${id}-model`}
              aria-label="Model for the new property"
              value={choice}
              disabled={busy}
              onChange={(event) => setChoice(event.target.value)}
              className="min-w-0 max-w-full rounded border border-stone-700 bg-[#0c0c0c] px-1.5 py-1 text-[9.5px] text-stone-300 outline-none focus:border-amber-300/50 disabled:opacity-50"
            >
              <option value="">Choose a model…</option>
              {models.map((version) => (
                <option key={version.id} value={version.id}>
                  v{version.versionNumber} · {version.filename}
                  {(version.workbookContext?.role ?? version.workbookRole ?? 'other') === 'other' ? ' · unassigned' : ''}
                </option>
              ))}
              <option value={UPLOAD_CHOICE}>Upload a workbook…</option>
            </select>
            {choice === UPLOAD_CHOICE ? (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  aria-label="Workbook for the new property"
                  className="hidden"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null)
                    event.target.value = ''
                  }}
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileRef.current?.click()}
                  className="rounded border border-stone-700 px-1.5 py-0.5 text-[9px] text-stone-300 hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-40"
                >
                  {file ? 'Change workbook' : 'Choose workbook'}
                </button>
                <span className="min-w-0 truncate text-stone-400" title={file?.name}>
                  {file ? file.name : 'No workbook chosen'}
                </span>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {note ? <p className="text-stone-400">{note}</p> : null}
      {shown ? <p role="alert" className="text-red-300">{shown}</p> : null}
      {busy && progress ? (
        <p aria-live="polite" className="flex items-center gap-1 text-stone-400">
          <Loader2 size={10} className="animate-spin" aria-hidden /> {progress}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-amber-400/40 px-1.5 py-0.5 text-[9px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
        >
          Add property
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="text-[9px] text-stone-400 hover:text-stone-200 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
